import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ApifyAlertSettingsResponse } from "./types";

export function ApifyAlertSettingsCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const settingsQuery = useQuery<ApifyAlertSettingsResponse>({
    queryKey: ["/api/admin/apify-alert-settings"],
  });
  const [thresholdUsd, setThresholdUsd] = useState<string>("");
  const [growthRatio, setGrowthRatio] = useState<string>("");
  const [minBaselineUsd, setMinBaselineUsd] = useState<string>("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !hydrated) {
      setThresholdUsd(String(settingsQuery.data.thresholdUsd));
      setGrowthRatio(String(settingsQuery.data.growthRatio));
      setMinBaselineUsd(String(settingsQuery.data.minBaselineUsd));
      setHydrated(true);
    }
  }, [settingsQuery.data, hydrated]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { thresholdUsd: number; growthRatio: number; minBaselineUsd: number }) => {
      return apiRequest("PUT", "/api/admin/apify-alert-settings", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apify-alert-settings"] });
      toast({ title: "Saved", description: "Apify spike alert settings updated." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to save", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const handleSave = () => {
    const t = Number(thresholdUsd);
    const g = Number(growthRatio);
    const m = Number(minBaselineUsd);
    if (!Number.isFinite(t) || t <= 0) {
      toast({ title: "Invalid threshold", description: "Threshold must be a positive number.", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(g) || g < 1) {
      toast({ title: "Invalid growth ratio", description: "Growth ratio must be at least 1.", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(m) || m < 0) {
      toast({ title: "Invalid baseline floor", description: "Minimum baseline must be 0 or greater.", variant: "destructive" });
      return;
    }
    saveMutation.mutate({ thresholdUsd: t, growthRatio: g, minBaselineUsd: m });
  };

  const handleResetToEnv = () => {
    const env = settingsQuery.data?.envDefaults;
    if (!env) return;
    setThresholdUsd(String(env.thresholdUsd));
    setGrowthRatio(String(env.growthRatio));
    setMinBaselineUsd(String(env.minBaselineUsd));
  };

  return (
    <Card className="p-4 space-y-4" data-testid="card-apify-alert-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-apify-alert-settings-title">Spike alert thresholds</h3>
          <p className="text-sm text-muted-foreground">
            Tune the daily Apify cost-spike detector. Saved values override the environment-variable defaults. Each Apify actor (plus an aggregate row across all actors) is checked once per UTC day.
          </p>
          {settingsQuery.data && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="text-apify-alert-settings-source">
              Currently active source: <strong>{settingsQuery.data.source === "database" ? "saved settings" : "environment defaults"}</strong>
              {settingsQuery.data.updatedAt && (
                <> · last updated {new Date(settingsQuery.data.updatedAt).toLocaleString()}</>
              )}
            </p>
          )}
        </div>
      </div>
      {settingsQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-destructive" data-testid="text-apify-alert-settings-error">Failed to load settings.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label htmlFor="input-apify-threshold" className="text-sm">Absolute threshold (USD/day)</Label>
              <Input
                id="input-apify-threshold"
                type="number"
                min="0"
                step="0.01"
                value={thresholdUsd}
                onChange={(e) => setThresholdUsd(e.target.value)}
                data-testid="input-apify-threshold"
              />
              <p className="text-xs text-muted-foreground">Alert when an actor's (or the aggregate) daily cost reaches this amount. Env default: ${settingsQuery.data?.envDefaults.thresholdUsd.toFixed(2)}.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="input-apify-growth-ratio" className="text-sm">Growth ratio (×)</Label>
              <Input
                id="input-apify-growth-ratio"
                type="number"
                min="1"
                step="0.1"
                value={growthRatio}
                onChange={(e) => setGrowthRatio(e.target.value)}
                data-testid="input-apify-growth-ratio"
              />
              <p className="text-xs text-muted-foreground">Alert when daily cost is at least this multiple of the prior 7-day average. Env default: {settingsQuery.data?.envDefaults.growthRatio}×.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="input-apify-min-baseline" className="text-sm">Min baseline for ratio (USD)</Label>
              <Input
                id="input-apify-min-baseline"
                type="number"
                min="0"
                step="0.01"
                value={minBaselineUsd}
                onChange={(e) => setMinBaselineUsd(e.target.value)}
                data-testid="input-apify-min-baseline"
              />
              <p className="text-xs text-muted-foreground">Skip the ratio rule when the baseline is below this. Env default: ${settingsQuery.data?.envDefaults.minBaselineUsd.toFixed(2)}.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              data-testid="button-save-apify-alert-settings"
            >
              {saveMutation.isPending ? "Saving..." : "Save settings"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleResetToEnv}
              disabled={saveMutation.isPending}
              data-testid="button-reset-apify-alert-settings"
            >
              Reset to env defaults
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
